import "@/App.css";
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ApplicationsPage from "@/components/applications/ApplicationsPage";
import AppRolesPage from "@/components/roles/appRoles/AppRolesPage";
import AppCustomerUserRolesPage from "@/components/roles/appCustomerUserRoles/AppCustomerUserRolesPage";
import AppPrivilegesPage from "@/components/privileges/appPrivileges/AppPrivilegesPage";
import AppRolePrivilegesPage from "@/components/privileges/appRolePrivileges/AppRolePrivilegesPage";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import Layout from "@/components/layout/Layout";
import AppCustomersPage from "@/components/customers/AppCustomersPage";
import AppCustomerUsersPage from "@/components/users/app-customer-users/AppCustomerUsersPage";
import AppCustomerUserRolePrivilegesPage from "@/components/privileges/appCustomerUserRolePrivileges/AppCustomerUserRolePrivilegesPage";
//msal
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import Logout from "@/authentication/logout";
import Audits from "@/components/audits/Audits";
import HomePage from "@/components/home/HomePage";
import CustomersPage from "@/components/customers/CustomersPage";
import CustomerUsersPage from "@/components/customers/customer_users/CustomerUsersPage";
import CustomerAppRegistrationsPage from "@/components/customers/customer_app_registrations/CustomerAppRegistrationsPage";
import UsersPage from "@/components/users/UsersPage";
import FilesPage from "@/components/files/FilesPage";
import FileDetailsPage from "@/components/files/FileDetailsPage";
import JobEngineLayout from "@/components/job-engine/JobEngineLayout";
import JobEngineDashboardPage from "@/components/job-engine/JobEngineDashboardPage";
import JobsListPage from "@/components/job-engine/JobsListPage";
import JobDetailPage from "@/components/job-engine/JobDetailPage";
import RecurringListPage from "@/components/job-engine/RecurringListPage";
import RecurringFormPage from "@/components/job-engine/RecurringFormPage";
import DeadLettersListPage from "@/components/job-engine/DeadLettersListPage";
import DeadLetterDetailPage from "@/components/job-engine/DeadLetterDetailPage";
import WorkbenchPage from "@/components/job-engine/WorkbenchPage";
import WebhooksInboundPage from "@/components/webhooks/WebhooksInboundPage";
import WebhooksOutboundPage from "@/components/webhooks/WebhooksOutboundPage";
import IntegrationDashboardPage from "@/components/integrations/dashboard/IntegrationDashboardPage";
import IntegrationCatalogPage from "@/components/integrations/catalog/IntegrationCatalogPage";
import IntegrationConfigurationsPage from "@/components/integrations/configurations/IntegrationConfigurationsPage";
import IntegrationPricingPage from "@/components/integrations/pricing/IntegrationPricingPage";
import IntegrationUsagePage from "@/components/integrations/usage/IntegrationUsagePage";
import CustomerDefaultsPage from "@/components/integrations/customer-defaults/CustomerDefaultsPage";
import IntegrationFeedbackPage from "@/components/integrations/feedback/IntegrationFeedbackPage";
import IntegrationAuditsPage from "@/components/integrations/audits/IntegrationAuditsPage";
import CachingPage from "@/components/caching/CachingPage";
import TelemetrySearchPage from "@/components/telemetry/search/TelemetrySearchPage";
import TelemetryDetailPage from "@/components/telemetry/detail/TelemetryDetailPage";
import CortexCloningPage from "@/components/cortex-cloning/CortexCloningPage";
import PasswordResetPage from "@/components/password-reset/PasswordResetPage";
import PasswordResetAuditsPage from "@/components/password-reset/PasswordResetAuditsPage";
import RenameCompanyPage from "@/components/rename-company/RenameCompanyPage";
import RenameCompanyAuditsPage from "@/components/rename-company/RenameCompanyAuditsPage";
import { ApiProvider } from "@pcs/telemetry-api";
import ApiAuthWrapper from "@/components/ApiAuthWrapper";
import { Configuration } from "@/config";

initializeIcons();

const { config: { TELEMETRY_API_ENDPOINT } } = new Configuration();

function App() {
  return (
    <div className="container">
      <Router>
        <Layout>
          <AuthenticatedTemplate>
            <ApiProvider apiBaseUrl={TELEMETRY_API_ENDPOINT}>
              <ApiAuthWrapper>
                <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/home" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<HomePage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route
                path="/customers/:customerId/users"
                element={<CustomerUsersPage />}
              />
              <Route
                path="/customers/:customerId/open-api-integrations"
                element={<CustomerAppRegistrationsPage />}
              />
              <Route path="/applications" element={<ApplicationsPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route
                path="/applications/:appId/customers"
                element={<AppCustomersPage />}
              />
              <Route
                path="/applications/:appId/customers/:customerId/users"
                element={<AppCustomerUsersPage />}
              />
              <Route
                path="/applications/:appId/roles"
                element={<AppRolesPage />}
              />
              <Route
                path="/applications/:appId/privileges"
                element={<AppPrivilegesPage />}
              />
              <Route
                path="/applications/:appId/customers/:customerId/users/:userId/roles"
                element={<AppCustomerUserRolesPage />}
              />
              <Route
                path="/applications/:appId/roles/:roleId/privileges"
                element={<AppRolePrivilegesPage />}
              />
              <Route
                path="/applications/:appId/customers/:customerId/users/:userId/roles/:roleId/privileges"
                element={<AppCustomerUserRolePrivilegesPage />}
              />
              <Route path="/logout" element={<Logout />} />
              <Route path="/audits" element={<Audits />} />
              <Route path="/files" element={<FilesPage />} />
              <Route path="/files/:internalFileId" element={<FileDetailsPage />} />
              <Route path="/job-engine" element={<JobEngineLayout />}>
                <Route index element={<JobEngineDashboardPage />} />
                <Route path="jobs" element={<JobsListPage />} />
                <Route path="jobs/:jobId" element={<JobDetailPage />} />
                <Route path="recurring" element={<RecurringListPage />} />
                <Route path="recurring/new" element={<RecurringFormPage />} />
                <Route path="recurring/:recurringId" element={<RecurringFormPage />} />
                <Route path="dead-letters" element={<DeadLettersListPage />} />
                <Route path="dead-letters/:deadLetterId" element={<DeadLetterDetailPage />} />
                <Route path="workbench" element={<WorkbenchPage />} />
              </Route>
              <Route path="/webhooks/inbound" element={<WebhooksInboundPage />} />
              <Route path="/webhooks/outbound" element={<WebhooksOutboundPage />} />
              <Route path="/integrations/dashboard" element={<IntegrationDashboardPage />} />
              <Route path="/integrations/catalog" element={<IntegrationCatalogPage />} />
              <Route path="/integrations/configurations" element={<IntegrationConfigurationsPage />} />
              <Route path="/integrations/pricing" element={<IntegrationPricingPage />} />
              <Route path="/integrations/usage" element={<IntegrationUsagePage />} />
              <Route path="/integrations/customer-defaults" element={<CustomerDefaultsPage />} />
              <Route path="/integrations/feedback" element={<IntegrationFeedbackPage />} />
              <Route path="/integrations/audits" element={<IntegrationAuditsPage />} />
              <Route path="/caching" element={<CachingPage />} />
              <Route path="/telemetry/search" element={<TelemetrySearchPage />} />
              <Route path="/telemetry/detail/:id" element={<TelemetryDetailPage />} />
              <Route path="/cortex-cloning" element={<CortexCloningPage />} />
              <Route path="/password-reset" element={<PasswordResetPage />} />
              <Route path="/password-reset/audits" element={<PasswordResetAuditsPage />} />
              <Route path="/rename-company" element={<RenameCompanyPage />} />
              <Route path="/rename-company/audits" element={<RenameCompanyAuditsPage />} />
            </Routes>
              </ApiAuthWrapper>
            </ApiProvider>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate />
        </Layout>
      </Router>
    </div>
  );
}

export default App;
